import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import MuiCard from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import AppTheme from "../shared-theme/AppTheme";
import { useNavigate } from "react-router-dom";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import MuiAvatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import "../i18n/i18n";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, useColorScheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { getCurrentTenant } from "../config/tenantConfig";

const { apiBaseUrl: API_BASE_URL } = getCurrentTenant();

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
  },
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const Avatar = styled(MuiAvatar)(({ theme }) => ({
  width: 28,
  height: 28,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  color: (theme.vars || theme).palette.text.secondary,
  border: `1px solid ${(theme.vars || theme).palette.divider}`,
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(206, 3.80%, 63.70%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 71.10%, 51.20%, 0.50), hsl(220, 30%, 5%))",
    }),
  },
}));

function parseJwt(token) {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Invalid JWT format");
  const base64 =
    parts[1].replace(/-/g, "+").replace(/_/g, "/") +
    "==".slice(2 - (parts[1].length % 4));
  const json = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(json);
}

export default function SignIn(props) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { tenantConfig } = props;

  const [userNameError, setUserNameError] = React.useState(false);
  const [userNameErrorMessage, setUserNameErrorMessage] = React.useState("");
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
  const [companyNameError, setCompanyNameError] = React.useState(false);
  const [companyNameErrorMessage, setCompanyNameErrorMessage] =
    React.useState("");
  const [projectCodeError, setProjectCodeError] = React.useState(false);
  const [projectCodeErrorMessage, setProjectCodeErrorMessage] =
    React.useState("");
  const [language, setLanguage] = React.useState(i18n.language || "en");
  const [loading, setLoading] = React.useState(false);
  const [selectedProjectCode, setSelectedProjectCode] = React.useState(
    tenantConfig?.projectCode || ""
  );
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState("");
  const [dialogMessage, setDialogMessage] = React.useState("");

  const showErrorDialog = (title, message) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogOpen(true);
  };

  // On page load, force MUI color mode to light (once)
  function SystemModeSync() {
    const { setMode } = useColorScheme();
    React.useEffect(() => {
      setMode("light");
    }, [setMode]);
    return null;
  }

  // Show current resolved mode (light/dark) even if mode is set to system elsewhere
  // function ResolvedModeBadge() {
  //   const { mode, systemMode } = useColorScheme();
  //   const resolved = systemMode || mode || "light";
  //   return (
  //     <Chip
  //       size="small"
  //       variant="outlined"
  //       label={resolved === "dark" ? "Dark" : "Light"}
  //       sx={{ mr: 1 }}
  //     />
  //   );
  // }

  // Sayfa yüklendiğinde (login ekranına girildiğinde) localStorage temizlensin
  React.useEffect(() => {
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userName");
      localStorage.removeItem("adminId");
      localStorage.removeItem("objectData");
      localStorage.removeItem("enumData");
      localStorage.removeItem("userRoles");
      localStorage.removeItem("projectCode");
      localStorage.removeItem("companyName");
    } catch (e) {
      console.warn("localStorage clear failed", e);
    }
  }, []);

  // Dil seçiminin ilk yüklemede doğru görünmesi için useEffect ekleyelim
  React.useEffect(() => {
    if (i18n.language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  const isSubdomain = window.location.hostname.split(".").length > 2;

  const shouldFetchProjectCodes = !isSubdomain || !tenantConfig?.projectCode;

  const turkeyFlag = require("../assets/turkey.png");
  const ukFlag = require("../assets/united-kingdom.png");
  const germanFlag = require("../assets/german.png");
  const frenchFlag = require("../assets/france.png");
  const spanishFlag = require("../assets/es.png");

  React.useEffect(() => {
    if (!shouldFetchProjectCodes) {
      return;
    }

    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [shouldFetchProjectCodes]);

  // Determine tenant logo (supports both top-level and theme-scoped fields)
  const tenantLogoLight =
    tenantConfig?.logoLight || tenantConfig?.theme?.logoLight || null;
  const tenantLogoDark =
    tenantConfig?.logoDark || tenantConfig?.theme?.logoDark || null;
  const resolvedLogoSrc =
    theme.palette.mode === "dark"
      ? tenantLogoDark || tenantLogoLight
      : tenantLogoLight || tenantLogoDark;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateInputs()) {
      return;
    }
    const username = document.getElementById("userName").value;
    const password = document.getElementById("password").value;
    const client_id = document.getElementById("companyName").value;
    const projectCode = tenantConfig?.projectCode || selectedProjectCode;

    fetch(
      `${
        tenantConfig?.apiBaseUrl || API_BASE_URL
      }/${projectCode}/api/auth/login`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams([
          ["client_id", client_id],
          ["grant_type", "password"],
          ["username", username],
          ["password", password],
          ["projectCode", projectCode],
        ]).toString(),
      }
    )
      .then((response) => {
        // console.log("Login response status:", response.status);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // console.log("Login response data:", data);
        if (data.returnCode === 2) {
          showErrorDialog("Giriş Hatası", data.exMessage);
          return;
        }

        const token = data.access_token || data.token || data.accessToken;

        if (token) {
          try {
            const decodedToken = parseJwt(token);
            // console.log("Decoded token:", decodedToken);

            if (decodedToken.adminId !== undefined) {
              localStorage.setItem(
                "adminId",
                parseInt(decodedToken.adminId, 10)
              );
            }

            if (decodedToken.azp !== client_id) {
              console.error("Token validation failed: Client ID mismatch");
              showErrorDialog(
                "Kimlik Doğrulama Hatası",
                t("signIn.errors.clientIdMismatch") ||
                  "Authentication failed: Client ID mismatch"
              );
              return;
            }

            const clientRoles =
              decodedToken.resource_access?.[client_id]?.roles;
            if (!clientRoles || clientRoles.length === 0) {
              console.error("Token validation failed: No roles assigned");
              showErrorDialog(
                "Rol Hatası",
                t("signIn.errors.noRolesAssigned") ||
                  "Authentication failed: No roles assigned"
              );
              return;
            }

            localStorage.setItem("authToken", token);
            localStorage.setItem("userName", username);
            localStorage.setItem("companyName", client_id);
            localStorage.setItem("projectCode", projectCode);
            localStorage.setItem("userRoles", JSON.stringify(clientRoles));

            const languageType =
              language === "tr"
                ? 1
                : language === "de"
                ? 3
                : language === "fr"
                ? 4
                : language === "es"
                ? 5
                : 2;

            fetch(
              `${API_BASE_URL}/${projectCode}/api/Objects?lettaLanguageType=${languageType}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            )
              .then((response) => {
                if (!response.ok) {
                  throw new Error(
                    `Object data fetch failed: ${response.status}`
                  );
                }
                return response.json();
              })
              .then((responseData) => {
                // console.log("Object data fetched successfully");
                const objectData = responseData.data || [];
                localStorage.setItem("objectData", JSON.stringify(objectData));

                return fetch(`${API_BASE_URL}/${projectCode}/api/Enums`, {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                });
              })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`Enum data fetch failed: ${response.status}`);
                }
                return response.json();
              })
              .then((responseData) => {
                // console.log("Enum data fetched successfully");
                const enumData = responseData.data || [];
                localStorage.setItem("enumData", JSON.stringify(enumData));
              })
              .catch((error) => {
                console.error("Error fetching data:", error);
              })
              .finally(() => {
                // console.log(
                //   "Authentication successful, redirecting to dashboard"
                // );
                navigate("/dashboard");
              });
          } catch (error) {
            console.error("Error decoding token:", error);
            showErrorDialog(
              "Token Hatası",
              t("signIn.errors.invalidToken") || "Invalid token format"
            );
          }
        } else {
          console.warn("No token found in response");
          // const token = "temp_token_" + new Date().getTime();
          // localStorage.setItem("authToken", token);
          // localStorage.setItem("userName", username);
          // localStorage.setItem("projectCode", projectCode);

          // // Navigate to dashboard directly without trying to fetch object data
          // console.log("Navigating to dashboard with temporary token");
          // navigate("/dashboard");
        }
      })
      .catch((err) => {
        console.error("Login error:", err);
        showErrorDialog(
          "Giriş Hatası",
          t("signIn.errors.loginFailed") || "Login failed. Please try again."
        );
      });
  };

  const validateInputs = () => {
    const userName = document.getElementById("userName");
    const password = document.getElementById("password");
    const companyName = document.getElementById("companyName");
    let isValid = true;

    if (!userName.value) {
      setUserNameError(true);
      setUserNameErrorMessage(t("signIn.errors.userNameRequired"));
      isValid = false;
    } else {
      setUserNameError(false);
      setUserNameErrorMessage("");
    }

    if (!password.value) {
      setPasswordError(true);
      setPasswordErrorMessage(t("signIn.errors.passwordRequired"));
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage("");
    }

    if (!companyName.value) {
      setCompanyNameError(true);
      setCompanyNameErrorMessage(t("signIn.errors.companyNameRequired"));
      isValid = false;
    } else {
      setCompanyNameError(false);
      setCompanyNameErrorMessage("");
    }

    if (!tenantConfig?.projectCode) {
      if (!selectedProjectCode) {
        setProjectCodeError(true);
        setProjectCodeErrorMessage(t("signIn.errors.projectCodeRequired"));
        isValid = false;
      } else {
        setProjectCodeError(false);
        setProjectCodeErrorMessage("");
      }
    }

    return isValid;
  };

  return (
    <AppTheme {...props}>
      {/* Force color mode to follow the OS on this page */}
      <SystemModeSync />
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <Stack
          direction="row"
          spacing={1}
          sx={{
            width: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
            marginBottom: 2,
            paddingRight: 1,
          }}
        >
          {/* <ResolvedModeBadge /> */}
          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={(event, newLanguage) => {
              if (newLanguage !== null) {
                // console.log(
                //   "Language changing from",
                //   language,
                //   "to",
                //   newLanguage
                // );
                setLanguage(newLanguage);
                i18n.changeLanguage(newLanguage);
              }
            }}
            aria-label="language selection"
            size="small"
            sx={{
              height: 36,
              "& .MuiToggleButtonGroup-grouped": {
                minWidth: "32px",
                padding: "4px 6px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                "&:not(:first-of-type)": {
                  borderLeft: "1px solid",
                  borderLeftColor: theme.palette.divider,
                  borderRadius: 0,
                },
                "&:first-of-type": {
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                },
                "&:last-of-type": {
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                },
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              },
            }}
          >
            <ToggleButton value="tr" aria-label="Turkish">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="Turkish" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={turkeyFlag}
                    alt="Turkish Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  TR
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="en" aria-label="English">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="English" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={ukFlag}
                    alt="UK Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  EN
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="de" aria-label="German">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="German" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={germanFlag}
                    alt="German Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  DE
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="fr" aria-label="French">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="French" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={frenchFlag}
                    alt="French Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  FR
                </Typography>
              </Box>
            </ToggleButton>
            <ToggleButton value="es" aria-label="Spanish">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Avatar alt="Spanish" sx={{ width: 18, height: 18 }}>
                  <Box
                    component="img"
                    src={spanishFlag}
                    alt="Spanish Flag"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Avatar>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: "bold" }}>
                  ES
                </Typography>
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Card variant="outlined">
          {tenantConfig?.name && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {resolvedLogoSrc ? (
                <Box
                  component="img"
                  src={resolvedLogoSrc}
                  alt={`${tenantConfig.name} Logo`}
                  sx={{
                    height: 70,

                    mr: 2,
                  }}
                />
              ) : (
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{
                    color:
                      theme.palette.mode === "dark"
                        ? "#FFF"
                        : theme.palette.primary.main,
                    textShadow:
                      theme.palette.mode === "dark"
                        ? "0px 2px 4px rgba(0, 0, 0, 0.2)"
                        : "none",
                    transition: "color 0.3s ease",
                    // Apply tenant-specific text styles
                    ...(tenantConfig.textStyle || {}),
                  }}
                >
                  {tenantConfig.name}
                </Typography>
              )}
            </Box>
          )}

          <Typography
            variant="h4"
            sx={{ textAlign: "left", fontWeight: "bold", py: 2 }}
          >
            {/* {t("signIn.title")} */}
          </Typography>
          <Divider />
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="userName">{t("signIn.userName")}</FormLabel>
              <TextField
                error={userNameError}
                helperText={userNameErrorMessage}
                id="userName"
                type="text"
                name="userName"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={userNameError ? "error" : "primary"}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="password">{t("signIn.password")}</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                type="password"
                id="password"
                required
                fullWidth
                variant="outlined"
                color={passwordError ? "error" : "primary"}
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="companyName">
                {t("signIn.companyName")}
              </FormLabel>
              <TextField
                error={companyNameError}
                helperText={companyNameErrorMessage}
                id="companyName"
                type="text"
                name="companyName"
                required
                fullWidth
                variant="outlined"
                color={companyNameError ? "error" : "primary"}
              />
            </FormControl>

            {!tenantConfig?.projectCode && (
              <FormControl error={projectCodeError}>
                <FormLabel htmlFor="projectCode">
                  {t("signIn.projectCode")}
                </FormLabel>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <TextField
                    id="projectCode"
                    value={selectedProjectCode}
                    onChange={(e) => {
                      setSelectedProjectCode(e.target.value);
                      setProjectCodeError(false);
                      setProjectCodeErrorMessage("");
                    }}
                    // placeholder={t("signIn.enterProjectCode")}
                    fullWidth
                    error={projectCodeError}
                    required
                    variant="outlined"
                    color={projectCodeError ? "error" : "primary"}
                  />
                )}
                {projectCodeError && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {projectCodeErrorMessage}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* {tenantConfig?.projectCode && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("signIn.projectCode")}: {tenantConfig.projectCode}
                </Typography>
              </Box>
            )} */}

            <Button type="submit" fullWidth variant="contained" sx={{ mt: 6 }}>
              {t("signIn.loginButton")}
            </Button>
            {tenantConfig?.name === "LettaIoT" && (
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate("/register")}
              >
                {t("signIn.signUpButton")}
              </Button>
            )}
            {/* <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              onClick={() =>
                window.open(
                  "https://173.249.28.101:8000/WWMS/api/Project",
                  "_blank",
                  "width=800,height=600,popup,noopener,noreferrer"
                )
              }
            >
              {t("signIn.accessApiEndpoint") || "Access API Endpoint"}
            </Button> */}
          </Box>
        </Card>
      </SignInContainer>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
      >
        <DialogTitle id="error-dialog-title">{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography id="error-dialog-description">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            color="primary"
            autoFocus
          >
            {t("common.ok") || "OK"}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
}
